import { gql } from "@apollo/client";

/* ============================================================================
 * NUSwap Research Platform — GraphQL operations
 *
 * These mirror the backend SDL in nce-backend/types/AdminWave.js. When the
 * backend schema changes, update the matching operation here.
 *
 * Fields shared across queries are kept inline (rather than fragments) to match
 * the existing query style in this folder.
 * ==========================================================================*/

/* ----------------------------- Shared ---------------------------------- */

export const ME_QUERY = gql`
    query Me {
        me { id email isAdmin }
    }
`;

/* --------------------- Participant: wave bootstrap ---------------------- */

// The single source of truth for what dark pattern to apply and which arm the
// participant is in. Call on every trading screen load. Returns null when the
// user is not enrolled in a running wave. `flags` is a JSON string — parse it.
export const MY_WAVE_CONFIG = gql`
    query MyWaveConfig {
        myWaveConfig {
            wave_id
            dark_pattern
            phase
            current_arm
            flags
        }
    }
`;

/* ------------- Participant: Wave E obstruction close lifecycle ---------- */

// Call when the participant taps "Close Position" in Phase 2 or 3. The server
// resolves wave/session/arm — the frontend never sends these. Store attempt_id.
export const INITIATE_POSITION_CLOSE = gql`
    mutation InitiatePositionClose($positionId: ID!) {
        initiatePositionClose(positionId: $positionId) {
            attempt_id
            position_id
            intervention_arm
            initiated_at
        }
    }
`;

// Call when the participant cancels the exit flow or the 120s timer elapses.
// pnlAtAbandonment is optional — the abandonment is still recorded without it.
// closeFlowStepReached (1–4) is the highest step reached before abandoning in the
// four-step flow; omit for Arm B single-step closes (there is no multi-step flow).
export const ABANDON_POSITION_CLOSE = gql`
    mutation AbandonPositionClose($attemptId: ID!, $pnlAtAbandonment: Float, $closeFlowStepReached: Int) {
        abandonPositionClose(attemptId: $attemptId, pnlAtAbandonment: $pnlAtAbandonment, closeFlowStepReached: $closeFlowStepReached) {
            attempt_id
            pnl_at_abandonment
            close_flow_step_reached
            abandoned_at
        }
    }
`;

// Call when the participant finishes all steps and the position is closed.
export const COMPLETE_POSITION_CLOSE = gql`
    mutation CompletePositionClose($attemptId: ID!, $pnlAtClose: Float) {
        completePositionClose(attemptId: $attemptId, pnlAtClose: $pnlAtClose) {
            attempt_id
            pnl_at_close
            executed_at
        }
    }
`;

/* -------- Participant: Wave E Arm C symmetric entry (open) lifecycle ---- */

// Arm C only, Phase 3 only. Call when an Arm C participant taps Buy — BEFORE
// submitting the order to the engine. The server assigns/validates the arm and
// returns attempt_id. On the "No active session" error, fall back to a normal
// buy (see endpointsforfrontend.md §5k).
export const INITIATE_POSITION_OPEN = gql`
    mutation InitiatePositionOpen($positionSymbol: String!, $quantity: Float!) {
        initiatePositionOpen(positionSymbol: $positionSymbol, quantity: $quantity) {
            attempt_id
            position_symbol
            quantity
            intervention_arm
            initiated_at
        }
    }
`;

// Call when the participant cancels the entry flow or the 120s timer elapses.
// openFlowStepReached (1–4) is the highest entry step reached before abandoning.
export const ABANDON_POSITION_OPEN = gql`
    mutation AbandonPositionOpen($attemptId: ID!, $openFlowStepReached: Int) {
        abandonPositionOpen(attemptId: $attemptId, openFlowStepReached: $openFlowStepReached) {
            attempt_id
            open_flow_step_reached
            abandoned_at
        }
    }
`;

// Call when the participant finishes all 4 entry steps. AFTER this resolves,
// submit the actual buy order to the matching engine (§5m).
export const COMPLETE_POSITION_OPEN = gql`
    mutation CompletePositionOpen($attemptId: ID!, $pnlAtOpen: Float) {
        completePositionOpen(attemptId: $attemptId, pnlAtOpen: $pnlAtOpen) {
            attempt_id
            pnl_at_open
            executed_at
        }
    }
`;

/* ------------ Participant: Wave E Arm A disclosure acknowledgment ------- */

// Call once per session when an Arm A participant dismisses the session-start
// disclosure modal ("I understand"). Idempotent — safe to call on reconnect.
// The server also logs a max_modal_response activity event internally, so do
// NOT separately call logParticipantActivity for this (§5o).
export const ACKNOWLEDGE_ARM_A_DISCLOSURE = gql`
    mutation AcknowledgeArmADisclosure {
        acknowledgeArmADisclosure {
            shown_at
        }
    }
`;

/* --------------------- Participant: activity logging ------------------- */

// Fire-and-forget after user-driven events the server can't infer on its own:
//   "trade" — after every successful engine order submission
//   "foreground_with_interaction" — on app resume + first interaction
// Never await this in a way that delays a trade or user-visible action (§5p).
export const LOG_PARTICIPANT_ACTIVITY = gql`
    mutation LogParticipantActivity($eventType: String!) {
        logParticipantActivity(eventType: $eventType) {
            event_id
            event_type
        }
    }
`;

/* --------------------------- Admin: waves ------------------------------ */

export const LIST_WAVES = gql`
    query ListWaves($contestId: ID) {
        listWaves(contestId: $contestId) {
            wave_id contest_id name dark_pattern status current_phase
            phase_started_at session_window_seconds start_date end_date created_at
            phase1_duration_seconds phase2_duration_seconds phase3_duration_seconds
        }
    }
`;

export const WAVE_DETAIL = gql`
    query WaveDetail($waveId: ID!) {
        waveDetail(waveId: $waveId) {
            wave {
                wave_id contest_id name dark_pattern status current_phase
                phase_started_at session_window_seconds start_date end_date created_at
                phase1_duration_seconds phase2_duration_seconds phase3_duration_seconds
            }
            currentFlags { id flag_key new_value old_value changed_at changed_by }
            recentSessions { session_id wave_id window_start window_end phase }
        }
    }
`;

// Phases are automatic (derived from start_date + the phase*DurationSeconds
// offsets) — there is still no manual phase-advance. The three duration args
// are optional; omit all of them to get the standard 14/14/42-day split.
// Pass shorter values (e.g. minutes) to spin up a short test wave.
// Note: session_window_seconds is not an argument — the activity-based session
// engine (BoundaryDetector) uses a fixed 30-min inactivity gap, not a
// per-wave configurable window; the backend hardcodes the stored column.
export const CREATE_WAVE = gql`
    mutation CreateWave(
        $name: String!, $darkPattern: String!, $startDate: String!,
        $phase1DurationSeconds: Int, $phase2DurationSeconds: Int, $phase3DurationSeconds: Int
    ) {
        createWave(
            name: $name, darkPattern: $darkPattern, startDate: $startDate,
            phase1DurationSeconds: $phase1DurationSeconds,
            phase2DurationSeconds: $phase2DurationSeconds,
            phase3DurationSeconds: $phase3DurationSeconds
        ) {
            wave_id contest_id name dark_pattern status current_phase
            start_date end_date session_window_seconds created_at
            phase1_duration_seconds phase2_duration_seconds phase3_duration_seconds
        }
    }
`;

// `confirmed` must be true when the wave status is "running" — otherwise the
// backend throws, prompting the frontend to show a warning dialog first.
export const TOGGLE_WAVE_FLAG = gql`
    mutation ToggleWaveFlag($waveId: ID!, $flagKey: String!, $value: String!, $confirmed: Boolean) {
        toggleWaveFlag(waveId: $waveId, flagKey: $flagKey, value: $value, confirmed: $confirmed) {
            id flag_key old_value new_value changed_at changed_by
        }
    }
`;

export const WAVE_FLAG_HISTORY = gql`
    query WaveFlagHistory($waveId: ID!, $limit: Int, $offset: Int) {
        waveFlagHistory(waveId: $waveId, limit: $limit, offset: $offset) {
            id flag_key old_value new_value changed_by changed_at
        }
    }
`;

/* -------------------------- Admin: sessions ---------------------------- */

export const WAVE_SESSIONS = gql`
    query WaveSessions($waveId: ID!, $limit: Int, $offset: Int) {
        waveSessions(waveId: $waveId, limit: $limit, offset: $offset) {
            session_id wave_id window_start window_end phase
        }
    }
`;

/* ---------------------------- Admin: arms ------------------------------ */

export const PARTICIPANT_ARMS = gql`
    query ParticipantArms($waveId: ID!, $sessionId: ID!) {
        participantArms(waveId: $waveId, sessionId: $sessionId) {
            participant_id session_id wave_id arm retained_or_reassigned
        }
    }
`;

export const WAVE_ARM_COUNTS = gql`
    query WaveArmCounts($waveId: ID!) {
        waveArmCounts(waveId: $waveId) { arm count }
    }
`;

export const WAVE_ALL_PARTICIPANT_ARMS = gql`
    query WaveAllParticipantArms($waveId: ID!) {
        waveAllParticipantArms(waveId: $waveId) {
            participant_id session_id wave_id arm retained_or_reassigned
        }
    }
`;

/* ------------------------ Admin: harm metrics -------------------------- */

export const WAVE_PARTICIPANT_HARM_METRICS = gql`
    query WaveParticipantHarmMetrics($waveId: ID!) {
        waveParticipantHarmMetrics(waveId: $waveId) {
            participant_id arm trade_count total_pnl
            avg_pnl_per_trade largest_single_loss losing_trade_count
        }
    }
`;

export const WAVE_SESSION_HARM_METRICS = gql`
    query WaveSessionHarmMetrics($waveId: ID!) {
        waveSessionHarmMetrics(waveId: $waveId) {
            session_id arm trade_count total_pnl avg_pnl losing_trades
        }
    }
`;

/* ------------- Admin: Wave E obstruction research data ----------------- */

export const WAVE_CLOSE_ATTEMPTS = gql`
    query WaveCloseAttempts($waveId: ID!) {
        waveCloseAttempts(waveId: $waveId) {
            attempt_id participant_id session_id position_id phase intervention_arm
            abandonment_flag pnl_at_abandonment pnl_at_close
            initiated_at abandoned_at executed_at
        }
    }
`;

/* --------------------------- Admin: events ----------------------------- */

export const WAVE_EVENTS_SUB = gql`
    subscription WaveEvents($waveId: ID!) {
        waveEvents(waveId: $waveId) { waveId type payload occurredAt }
    }
`;

export const RECENT_WAVE_EVENTS = gql`
    query RecentWaveEvents($waveId: ID!, $limit: Int) {
        recentWaveEvents(waveId: $waveId, limit: $limit) { waveId type payload occurredAt }
    }
`;

/* ------------------------- Admin: super admin -------------------------- */

export const GRANT_ADMIN = gql`
    mutation GrantAdmin($userId: ID!) {
        grantAdmin(userId: $userId)
    }
`;
